import {
  doc,
  getDoc,
  getDocs,
  collection,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { firebaseFirestore } from '@/infrastructure/firebase/firebaseFirestore.js';
import { type TestSession } from '@k-ucap/shared';
import { getCurrentEnvironment, buildConverter, toTimestampString } from './firestoreUtils.js';
import type { SessionRepository } from '@/infrastructure/repositories/RepositoryInterfaces.js';

const TEST_SESSIONS = 'testSessions';

const converter = buildConverter<TestSession>(
  (data) =>
    ({
      ...data,
      startedAt: toTimestampString(data.startedAt) ?? new Date().toISOString(),
      endedAt: toTimestampString(data.endedAt) ?? undefined,
      createdAt: toTimestampString(data.createdAt) ?? new Date().toISOString(),
      updatedAt: toTimestampString(data.updatedAt) ?? new Date().toISOString(),
    }) as TestSession,
  (model) => ({ ...model, createdAt: serverTimestamp(), updatedAt: serverTimestamp() })
);

export class FirestoreSessionRepository implements SessionRepository {
  async findAll(): Promise<TestSession[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, TEST_SESSIONS),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('createdAt', 'desc'),
      limit(100)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findById(id: string): Promise<TestSession | undefined> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const snap = await getDoc(doc(db, TEST_SESSIONS, id).withConverter(converter));
    return snap.data();
  }

  async findByProjectId(projectId: string): Promise<TestSession[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, TEST_SESSIONS),
      where('projectId', '==', projectId),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('createdAt', 'desc'),
      limit(100)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findBySimulationTestId(simulationTestId: string): Promise<TestSession[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, TEST_SESSIONS),
      where('simulationTestId', '==', simulationTestId),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('createdAt', 'desc'),
      limit(100)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async save(session: TestSession): Promise<TestSession> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const data = { ...session, environment: getCurrentEnvironment() };
    const existing = await this.findById(session.id);
    await setDoc(doc(db, TEST_SESSIONS, session.id).withConverter(converter), data);
    return {
      ...data,
      createdAt: existing?.createdAt ?? session.createdAt,
      updatedAt: new Date().toISOString(),
    };
  }

  async delete(id: string): Promise<void> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    await deleteDoc(doc(db, TEST_SESSIONS, id));
  }
}

export const createFirestoreSessionRepository = (): FirestoreSessionRepository => new FirestoreSessionRepository();
